import React, { useState, useEffect, useRef } from 'react';
import { FormError } from 'components';
import {
  Button,
  Form,
  TextInput,
  Dropdown,
  InlineNotification
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { OptionItem, OrgTenant } from 'types';
import {
  setAddMemberModelOpenStart,
  setEditMemberModelOpenStart
} from 'redux/member/action';
import {
  fetchAddMemberRequest,
  selectType,
  selectData,
  setEditMemberRequest,
  selectOrganisationTenants,
  selectOrganisationProfile
} from 'redux/member';
import { getOrganisationType } from 'services/member.service';
import { AddMemberSchema, EditMemberSchema } from 'utilities/validation';
import { fetchOrgProfileStart } from 'redux/member';
import { orderBy, uniq } from 'lodash';

const initializeWith = (data: any) => {
  return data
    ? { ...data }
    : {
        name: '',
        primaryContactEmail: '',
        utilityId: ''
      };
};

const AddMemberForm = ({ params }: any) => {
  const dispatch: any = useDispatch();
  const orgInputRef = useRef(null);
  const formRef = useRef(null);
  const dropdownRef = useRef(null);

  const [orgTypes, setOrgTypes] = useState<Array<any>>([{ id: 0, text: '' }]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [orgTenantItems, setOrgTenantItems] = useState<OptionItem[]>([]);
  const [selectedOrgTypes, setSelectedOrgType] = useState<any>('');
  const [selectedOrgTenant, setSelectedOrgTenant] = useState<OptionItem>();
  const [dropdownDirection, setDropdownDirection] = useState<
    'bottom' | 'top' | undefined
  >('bottom');
  const [linkedUsers, setLinkedUsers] = useState<Array<any>>([
    { id: 0, text: '' }
  ]);

  const orgProfile: any = useSelector(selectOrganisationProfile);
  const orgTenants = useSelector(selectOrganisationTenants);
  const formType: string = useSelector(selectType);
  const data: any = useSelector(selectData);

  useEffect(() => {
    formType === 'EDIT' &&
      data.id &&
      dispatch(
        fetchOrgProfileStart({
          organisationId: Number(data.id),
          params: {}
        })
      );
  }, [data, dispatch, formType]);

  useEffect(() => {
    const orgUsers = orgProfile?.Users?.map((item: any, index: any) => ({
      key: index,
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email
    }));
    setLinkedUsers(orgUsers);
  }, [orgProfile]);

  useEffect(() => {
    const fetchOrganisationTypes = async () => {
      try {
        const response = await getOrganisationType();
        if (response.error) throw new Error('API Error');
        const organisationTypes = response.map((item: any, index: any) => ({
          id: index,
          text: item.label,
          code: item.code
        }));
        setOrgTypes(organisationTypes);
      } catch (err) {
        setApiError(err.message);
      }
    };
    fetchOrganisationTypes();

    const orgInputElement: any = orgInputRef.current;
    if (orgInputElement) orgInputElement.focus();

    const dropdownDirectionDecision = () => {
      const formWrapperElement: any = formRef.current;
      const dropdownElement: any = dropdownRef.current;

      if (formWrapperElement && dropdownElement) {
        // 220px is the height of the dropdown which is 13.75rem (base 16px)
        // Any change to the base we need to recalculate this
        const expectedHeight =
          dropdownElement.getBoundingClientRect().top +
          dropdownElement.getBoundingClientRect().height +
          220;

        expectedHeight > formWrapperElement.getBoundingClientRect().bottom
          ? setDropdownDirection('top')
          : setDropdownDirection('bottom');
      }
    };

    window.addEventListener('resize', dropdownDirectionDecision);

    dropdownDirectionDecision();

    return () => {
      window.removeEventListener('resize', dropdownDirectionDecision);
    };
  }, []);

  useEffect(() => {
    if (formType === 'EDIT' && data?.organizationType) {
      const foundOrg = orgTypes.find(
        (item: any) => item.code === data.organizationType
      );

      foundOrg && setSelectedOrgType(foundOrg);
    }
  }, [orgTypes, formType, data]);

  useEffect(() => {
    if (formType === 'EDIT' && data?.tenant) {
      const foundTenant = orgTenantItems.find(
        (item: any) => item.label === data.tenant
      );

      foundTenant && setSelectedOrgTenant(foundTenant);
    }
  }, [orgTenantItems, formType, data]);

  useEffect(() => {
    if (Array.isArray(orgTenants) && orgTenants.length > 0) {
      const options = orgTenants.map((item: OrgTenant) => {
        const { id, name } = item;

        return {
          value: String(id),
          label: name.replace('DBYD ', '')
        };
      });
      setOrgTenantItems(orderBy(uniq(options), ['label'], ['asc']));
    }
  }, [orgTenants]);

  const handleCancel = () => {
    formType === 'EDIT'
      ? dispatch(setEditMemberModelOpenStart(false))
      : dispatch(setAddMemberModelOpenStart(false));
  };

  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur
  } = useFormik({
    enableReinitialize: true,
    initialValues: initializeWith(data),
    validationSchema: formType !== 'EDIT' ? AddMemberSchema : EditMemberSchema,
    onSubmit: (submitValues: any) => {
      let newMember: any = {
        name: submitValues.name,
        role: 'ASSETOWNER',
        primaryContactEmail: submitValues.primaryContactEmail,
        organisationType: selectedOrgTypes?.code,
        tenantId: selectedOrgTenant?.value
          ? Number(selectedOrgTenant.value)
          : undefined
      };
      if (submitValues.utilityId) {
        newMember = { ...newMember, utilityId: submitValues.utilityId };
      }

      formType === 'EDIT'
        ? dispatch(
            setEditMemberRequest({
              organisationId: submitValues.id,
              payload: newMember,
              params
            })
          )
        : dispatch(
            fetchAddMemberRequest({
              member: newMember,
              params
            })
          );
    }
  });
  const isAddEnabled =
    values.name &&
    (formType === 'EDIT' || values.primaryContactEmail) &&
    selectedOrgTypes?.text &&
    !errors.name &&
    (formType === 'EDIT' || !errors.primaryContactEmail) &&
    !errors.utilityId &&
    (formType !== 'EDIT' && Array.isArray(orgTenants) && orgTenants.length > 0
      ? selectedOrgTenant?.value
      : true);

  return (
    <>
      <div className={'add-member-modal display-block'}>
        <section className="add-member-modal-main" ref={formRef}>
          <Form className="add-member-form-wrapper" onSubmit={handleSubmit}>
            {apiError && (
              <InlineNotification
                className="inline-toast-container"
                key={'error'}
                kind={'error'}
                title={apiError}
                lowContrast={true}
                hideCloseButton={false}
                onCloseButtonClick={() => setApiError(null)}
              />
            )}
            <section className="add-member-form-item">
              <h1 className="add-member-form-main-heading">
                {formType === 'EDIT'
                  ? `Edit asset owner`
                  : `Create asset owner`}
              </h1>
            </section>
            <hr className="add-member-divider" />
            <section className="add-user-form-item organisation-name">
              <TextInput
                id="name"
                name="name"
                labelText="Name"
                helperText="Organisation or entity name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                placeholder="Organisation name"
                ref={orgInputRef}
              />
              {touched.name && errors.name ? (
                <FormError>{errors.name}</FormError>
              ) : null}
            </section>
            {formType !== 'EDIT' && (
              <section className="add-user-form-item">
                <TextInput
                  id="primaryContactEmail"
                  name="primaryContactEmail"
                  labelText="Send email invitation to"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.primaryContactEmail}
                  placeholder="Email address"
                />
                {touched.primaryContactEmail && errors.primaryContactEmail ? (
                  <FormError>{errors.primaryContactEmail}</FormError>
                ) : null}
              </section>
            )}

            <section className="add-user-form-item">
              <TextInput
                placeholder="Utility ID (Optional)"
                id="utilityId"
                name="utilityId"
                labelText="Utility ID"
                helperText="Usually a 5 or 6-digit number"
                readOnly={formType === 'EDIT' ? true : false}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.utilityId}
              />
              {touched.utilityId && errors.utilityId ? (
                <FormError>{errors.utilityId}</FormError>
              ) : null}
            </section>

            <section className="add-user-form-item">
              <Dropdown
                className="apply-filter-form-wrapper-role"
                id="add-member-role"
                titleText="Organisation type"
                label="Choose an option"
                items={orgTypes}
                itemToString={(item) => (item ? item.text : '')}
                onChange={(item) => setSelectedOrgType(item.selectedItem)}
                selectedItem={selectedOrgTypes}
                direction={dropdownDirection}
                ref={dropdownRef}
              />
            </section>
            {orgTenantItems.length > 0 && (
              <section className="add-user-form-item">
                <Dropdown
                  className="apply-filter-form-tenant"
                  id="add-member-tenant"
                  titleText="Organisation territory"
                  label="Choose an option"
                  items={orgTenantItems}
                  onChange={(item) =>
                    item.selectedItem !== null &&
                    setSelectedOrgTenant(item.selectedItem)
                  }
                  selectedItem={selectedOrgTenant}
                  direction={dropdownDirection}
                />
              </section>
            )}

            {formType === 'EDIT' && (
              <section className="add-member-form-item linked-users">
                <div className="title">Linked users</div>
                <div className="content">
                  {linkedUsers?.map((user: any) => (
                    <div key={user.id}>
                      {(user.firstName === null || user.lastName === null) && (
                        <div className="wrapper">
                          <div className="link">
                            <a href={`mailTo:${user.email}`}>
                              {`${user.email}`}
                            </a>
                          </div>
                          <span>(invited)</span>
                        </div>
                      )}

                      {user.firstName !== null && user.lastName !== null && (
                        <div className="wrapper">
                          <div className="link">
                            <a href={`mailTo:${user.email}`}>
                              {`${user.firstName} ${user.lastName}`}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="add-user-form-item">
              <div className="button-set">
                <Button
                  id="cancel"
                  name="cancel"
                  kind="secondary"
                  data-testid="cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  id="create"
                  name="create"
                  type="submit"
                  kind="primary"
                  data-testid="submit"
                  size="sm"
                  disabled={!isAddEnabled}
                >
                  {formType === 'EDIT' ? `Save` : `Create`}
                </Button>
              </div>
            </section>
          </Form>
        </section>
      </div>
    </>
  );
};
export default AddMemberForm;
